import React, { memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaBars } from 'react-icons/fa';

const RadarHeader = memo(({ 
  menuOpen, 
  setMenuOpen, 
  view, 
  menuRef,
  // Filter props (optional)
  companies = [],
  selectedCompany = '',
  onCompanyChange = null,
  contactSearchTerm = '',
  onContactSearchChange = null,
  accounts = [],
  contacts = [],
  transcriptCompanyId = '',
  onTranscriptCompanyChange = null,
  transcriptContactNames = '',
  onTranscriptContactChange = null
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleMenuSelect = (selectedView) => {
    // Map view names to route paths (relative navigation)
    const routeMap = {
      'Accounts': 'accounts',
      'Contacts': 'contacts',
      'Transcript Analyzer': 'transcripts',
      'Buyer Cards': 'buyer-cards'
    };
    
    const route = routeMap[selectedView];
    if (route) {
      // Use relative navigation since we're inside the Routes component
      navigate(route);
    }
    setMenuOpen(false);
  };

  // Determine which filters to show based on view - only show for Buyer Cards
  const showFilters = view === 'Buyer Cards';

  return (
    <div className="radar-header-row" style={{ 
      background: 'linear-gradient(120deg, rgba(241, 245, 249, 1) 3%, rgba(219, 234, 254, 1) 37%, rgba(237, 233, 254, 1) 100%)', 
      height: '9vh', 
      padding: '20px 0px 10px 10px', 
      boxShadow: '0 2px 8px rgba(0,0,0,0.03)', 
      marginBottom: 24, 
      alignItems: 'flex-start', 
      display: 'flex', 
      justifyContent: 'space-between' 
    }}>
      <div className="radar-header-left" style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ fontSize: 24, fontWeight: 600, padding: 0, margin: 0 }}>{view}</div>
      </div>
      <div className="radar-header-right" style={{ display: 'flex', gap: 12, alignItems: 'center', paddingRight: 10 }}>
        {/* Filters Section - Moved to right side */}
        {showFilters && (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* Company Filter - only for Buyer Cards */}
            {view === 'Buyer Cards' && (
              <select
                value={selectedCompany}
                onChange={(e) => {
                  if (onCompanyChange) {
                    onCompanyChange(e);
                  }
                }}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: 'white',
                  minWidth: '180px',
                  height: '36px',
                  cursor: 'pointer',
                  position: 'relative',
                  zIndex: 10,
                  appearance: 'auto',
                  WebkitAppearance: 'menulist',
                  MozAppearance: 'menulist'
                }}
              >
                <option value="">Select a Company</option>
                {companies.length > 0 ? (
                  companies.map((company) => (
                    <option key={company} value={company}>
                      {company}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No companies available</option>
                )}
              </select>
            )}
            
            {/* Contact Search Filter - only for Buyer Cards */}
            {view === 'Buyer Cards' && (
              <input
                type="text"
                placeholder={selectedCompany ? "Search by name, title, email, or phone..." : "Select a company first"}
                value={contactSearchTerm || ''}
                onChange={(e) => {
                  if (onContactSearchChange) {
                    onContactSearchChange(e.target.value);
                  }
                }}
                disabled={!selectedCompany}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: 'white',
                  minWidth: '250px',
                  height: '36px',
                  opacity: !selectedCompany ? 0.5 : 1,
                  cursor: !selectedCompany ? 'not-allowed' : 'text'
                }}
              />
            )}
          </div>
        )}
        <div className="radar-hamburger-wrapper" ref={menuRef} style={{ position: 'relative' }}>
          <button 
            className="radar-hamburger" 
            onClick={() => setMenuOpen(!menuOpen)} 
            style={{ 
              background: 'none', 
              border: 'none', 
              fontSize: 18, 
              cursor: 'pointer', 
              padding: '8px' 
            }}
          >
            <FaBars />
          </button>
          {menuOpen && (
            <>
              <div 
                className="radar-menu-overlay" 
                onClick={() => setMenuOpen(false)} 
                style={{ 
                  position: 'fixed', 
                  top: 0, 
                  left: 0, 
                  width: '100%', 
                  height: '100%', 
                  background: 'transparent', 
                  zIndex: 999 
                }} 
              />
              <div 
                className="radar-menu-dropdown" 
                style={{ 
                  position: 'absolute', 
                  top: '100%', 
                  right: 0, 
                  background: '#fff', 
                  borderRadius: '8px', 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)', 
                  zIndex: 1000, 
                  minWidth: '200px' 
                }}
              >
                <div 
                  className={`radar-menu-item ${view === 'Accounts' ? 'active' : ''}`} 
                  onClick={() => handleMenuSelect('Accounts')} 
                  style={{ 
                    padding: '12px 16px', 
                    cursor: 'pointer', 
                    fontSize: '14px', 
                    color: view === 'Accounts' ? '#2563eb' : '#333', 
                    backgroundColor: view === 'Accounts' ? '#f8f9fa' : 'transparent' 
                  }}
                >
                  Accounts
                </div>
                <div 
                  className={`radar-menu-item ${view === 'Contacts' ? 'active' : ''}`} 
                  onClick={() => handleMenuSelect('Contacts')} 
                  style={{ 
                    padding: '12px 16px', 
                    cursor: 'pointer', 
                    fontSize: '14px', 
                    color: view === 'Contacts' ? '#2563eb' : '#333', 
                    backgroundColor: view === 'Contacts' ? '#f8f9fa' : 'transparent' 
                  }}
                >
                  Contacts
                </div>
                <div 
                  className={`radar-menu-item ${view === 'Transcript Analyzer' ? 'active' : ''}`} 
                  onClick={() => handleMenuSelect('Transcript Analyzer')} 
                  style={{ 
                    padding: '12px 16px', 
                    cursor: 'pointer', 
                    fontSize: '14px', 
                    color: view === 'Transcript Analyzer' ? '#2563eb' : '#333', 
                    backgroundColor: view === 'Transcript Analyzer' ? '#f8f9fa' : 'transparent' 
                  }}
                >
                  Transcript Analyzer
                </div>
                <div 
                  className={`radar-menu-item ${view === 'Buyer Cards' ? 'active' : ''}`} 
                  onClick={() => handleMenuSelect('Buyer Cards')} 
                  style={{ 
                    padding: '12px 16px', 
                    cursor: 'pointer', 
                    fontSize: '14px', 
                    color: view === 'Buyer Cards' ? '#2563eb' : '#333', 
                    backgroundColor: view === 'Buyer Cards' ? '#f8f9fa' : 'transparent' 
                  }}
                >
                  Buyer Cards
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default RadarHeader;

RadarHeader.displayName = 'RadarHeader';
